import { useEffect } from 'react';
import isEqual from 'react-fast-compare';
import { FormProvider, useForm } from 'react-hook-form';
import { useReportMetricsContextV2 } from 'src/contexts/ReportMetricsContext/ReportMetricsContextV2';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { Colors } from 'src/core/themes/constants/colors';
import { ReportBuilderFilterInputV2 } from 'src/dialogs/ReportBuilderDialog/ReportBuilderFilterInputV2';
import { ReportConfigV2, useReportPermissions } from 'src/hooks/reportConfigs';

import * as styles from './ReportsFilterToolbar.css';

export const ReportsFilterToolbarV2 = ({
  reportConfig,
}: {
  reportConfig: ReportConfigV2;
}) => {
  const { isDarkMode } = useSiteTheme();
  const methods = useForm<ReportConfigV2>({
    defaultValues: reportConfig,
  });

  const { updateTempReportConfig } = useReportMetricsContextV2();
  const { canUpdateReport } = useReportPermissions(reportConfig);

  // Report config can be changed elsewhere like the table editor
  useEffect(() => {
    if (!isEqual(methods.formState.defaultValues, reportConfig)) {
      methods.reset(reportConfig);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportConfig]);

  const filters = methods.watch('request.filters');
  const hiddenFilterItemIds = methods.watch('hiddenFilterItemIds');
  const viewableFilterItemIds = methods.watch('viewableFilterItemIds');
  const editableFilterItemIds = methods.watch('editableFilterItemIds');

  const onSubmit = (data: ReportConfigV2) => {
    if (
      !isEqual(data.request.filters, reportConfig.request.filters) ||
      !isEqual(data.hiddenFilterItemIds, reportConfig.hiddenFilterItemIds) ||
      !isEqual(
        data.viewableFilterItemIds,
        reportConfig.viewableFilterItemIds
      ) ||
      !isEqual(data.editableFilterItemIds, reportConfig.editableFilterItemIds)
    ) {
      // We don't allow anyone to update the filter from the filter toolbar
      updateTempReportConfig(data, false /* shouldUpdateReportConfig */);
    }
  };

  useEffect(() => {
    methods.handleSubmit(onSubmit)();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters,
    hiddenFilterItemIds,
    viewableFilterItemIds,
    editableFilterItemIds,
  ]);

  return (
    <FormProvider {...methods}>
      <div className={styles.toolbarContainerV2}>
        <ReportBuilderFilterInputV2
          reportType={reportConfig.reportType}
          viewerMode={
            reportConfig.globalReportTypeId != null
              ? 'editFilter'
              : !canUpdateReport
                ? 'viewOnly'
                : 'editFilterEditability'
          }
          addFilterDropdownProps={{
            triggerClassName: styles.addFilterButton,
            iconsFill: isDarkMode ? Colors.dark060 : undefined,
            hideLabel: true,
          }}
          // Even owners can't edit from filter toolbar, so no point allowing to edit editability
          editabilityDropdownDisabled={true}
        />
      </div>
    </FormProvider>
  );
};
